'use client';

import type { ReactNode } from 'react';
import { Container } from '@/atoms/Container/Container';
import { TIMELINE_MAX_UNPRODUCTIVE_AUTO_LOADS } from '@/config/feed';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll/useInfiniteScroll';
import { usePostListKeyboard } from '@/hooks/usePostListKeyboard/usePostListKeyboard';
import { usePostNavigation } from '@/hooks/usePostNavigation/usePostNavigation';
import { TimelineEndMessage } from '@/molecules/Timeline/TimelineEndMessage';
import { TimelineError } from '@/molecules/Timeline/TimelineError';
import { TimelineErrorState } from '@/molecules/Timeline/TimelineErrorState';
import { TimelineLoadingMore } from '@/molecules/Timeline/TimelineLoadingMore';
import { TimelineLoadMore } from '@/molecules/Timeline/TimelineLoadMore';
import { TimelineStateWrapper } from '@/molecules/Timeline/TimelineStateWrapper/TimelineStateWrapper';
import { TimelineFeedItem } from './FeedItem/TimelineFeedItem';

interface TimelinePostsProps {
  postIds: string[];
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  emptyState?: ReactNode;
  trailingSlot?: ReactNode;
  showEndMessage?: boolean;
}

/**
 * TimelinePosts
 *
 * Presentational component that displays posts in a timeline with infinite scroll.
 * Receives all data and handlers from a parent component.
 *
 * The surface (TimelineFeedContent) wraps this in PostMainLayoutProvider so each
 * PostMain / nested reply inherits the active tags layout via context.
 */
export function TimelinePosts({
  postIds,
  loading,
  loadingMore,
  error,
  hasMore,
  loadMore,
  emptyState,
  trailingSlot,
  showEndMessage = true,
}: TimelinePostsProps) {
  // Filtering is client-side, so a load round can come back with nothing visible while the
  // stream still has more. Rounds that fail to grow the list are budgeted: past the budget
  // the sentinel stops and the manual Load more below takes over (#2523).
  const { sentinelRef, isStalled, resumeAutoLoad } = useInfiniteScroll({
    onLoadMore: loadMore,
    hasMore,
    isLoading: loadingMore,
    threshold: 3000,
    debounceMs: 20,
    itemCount: postIds.length,
    maxUnproductiveLoads: TIMELINE_MAX_UNPRODUCTIVE_AUTO_LOADS,
  });

  const { handlePostKeyDown } = usePostNavigation();
  const { setCardRef, onListKeyDown } = usePostListKeyboard();
  const hasListContent = postIds.length > 0 || trailingSlot != null;
  // Beside a trailing slot an empty list still renders, so a failed load is
  // reported there instead of the empty state.
  const trailingSlotError = postIds.length === 0 && trailingSlot != null ? error : null;
  const showEmptyMessageWithTrailingSlot =
    postIds.length === 0 && trailingSlot != null && emptyState != null && !trailingSlotError;
  const showMessageWithTrailingSlot = showEmptyMessageWithTrailingSlot || Boolean(trailingSlotError);

  return (
    <TimelineStateWrapper
      loading={loading}
      error={error}
      hasItems={hasListContent}
      hasMore={hasMore}
      stalled={isStalled}
      emptyComponent={emptyState}
    >
      <Container
        data-cy="timeline-container"
        overrideDefaults={showMessageWithTrailingSlot}
        className={showMessageWithTrailingSlot ? 'flex w-full flex-col gap-4' : undefined}
      >
        {trailingSlotError ? <TimelineErrorState message={trailingSlotError} /> : null}
        {showEmptyMessageWithTrailingSlot ? emptyState : null}
        <Container
          data-cy="timeline-posts"
          overrideDefaults
          role="feed"
          className="space-y-4"
          onKeyDown={onListKeyDown}
        >
          {postIds.map((postId, index) => (
            <TimelineFeedItem
              key={`main_${postId}`}
              postId={postId}
              index={index}
              totalCount={postIds.length}
              setCardRef={setCardRef}
              onPostKeyDown={handlePostKeyDown}
            />
          ))}

          {trailingSlot}

          {/* Suppressed while the list is empty: the wrapper's full skeleton is already
              the loading representation there (empty-but-hasMore chaining rounds). */}
          {postIds.length > 0 && loadingMore && <TimelineLoadingMore />}

          {error && postIds.length > 0 && <TimelineError message={error} />}

          {showEndMessage && !hasMore && !loadingMore && postIds.length > 0 && <TimelineEndMessage />}

          {hasMore && isStalled && !loadingMore && <TimelineLoadMore onLoadMore={resumeAutoLoad} />}

          {/* Infinite-scroll sentinel — only mounted (and given height) while there are
              more posts to observe for and auto-loading is not stalled, mirroring
              TimelineCardsPosts. Once the feed is fully loaded the observer detaches, so
              rendering it would just leave dead space below the list. */}
          {hasMore && !isStalled && <Container overrideDefaults className="h-5" ref={sentinelRef} />}
        </Container>
      </Container>
    </TimelineStateWrapper>
  );
}
